
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { useAuthUser } from './useAuthUser';

/**
 * Hook para gerenciar dados de tags
 * 
 * Este hook centraliza todas as operações relacionadas às tags:
 * - Buscar tags da clínica do usuário
 * - Criar novas tags
 * - Atualizar tags existentes
 * - Deletar tags
 * 
 * Utiliza as políticas RLS para garantir isolamento por clínica
 */

export interface Tag {
  id: string;
  nome: string;
  cor: string;
  clinica_id: string | null;
  created_at: string | null;
  updated_at: string | null;
  color_index?: number;
  color_hex?: string;
  whatsapp_label_id?: string;
  whatsapp_owner?: string;
  source?: string;
}

// Interface para criação de tag (campos obrigatórios)
export interface CreateTagData {
  nome: string;
  cor?: string;
  clinica_id: string;
}

// Hook para buscar todas as tags da clínica do usuário
export const useTags = (targetClinicaId?: string | null) => {
  const { userProfile } = useAuthUser();
  // null representa seleção vazia; somente a ausência do argumento usa a clínica da sessão.
  const clinicaId = targetClinicaId === undefined ? userProfile?.clinica_id : targetClinicaId;
  return useQuery({
    queryKey: ['tags', clinicaId],
    queryFn: async (): Promise<Tag[]> => {
      // Sem clínica, não consultar: o admin tem RLS amplo e veria tags de todas as clínicas.
      if (!clinicaId) return [];

      // Tabela tags: SELECT de todos os campos, filtrado por clinica_id e ordenado por nome.
      // O filtro limita as opções do admin; as permissões continuam validadas pelo RLS.
      const { data, error } = await supabase
        .from('tags')
        .select('*')
        .eq('clinica_id', clinicaId)
        .order('nome', { ascending: true });

      if (error) {
        console.error('❌ Erro ao buscar tags:', error);
        throw new Error(`Erro ao buscar tags: ${error.message}`);
      }

      console.log(`✅ ${data?.length || 0} tags encontradas`);
      return data || [];
    },
    enabled: Boolean(clinicaId),
    staleTime: 30000, // Cache por 30 segundos
  });
};

// Hook para criar nova tag
export const useCreateTag = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (tagData: CreateTagData): Promise<Tag> => {
      console.log('➕ Criando nova tag:', tagData.nome);

      const { data, error } = await supabase
        .from('tags')
        .insert([tagData])
        .select()
        .single();

      if (error) {
        console.error('❌ Erro ao criar tag:', error);
        throw new Error(`Erro ao criar tag: ${error.message}`);
      }

      console.log('✅ Tag criada com sucesso:', data.nome);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tags'] });
      toast.success('Tag criada com sucesso!');
    },
    onError: (error: Error) => {
      console.error('❌ Erro na criação da tag:', error);
      toast.error(`Erro ao criar tag: ${error.message}`);
    },
  });
};

// Hook para atualizar tag existente
export const useUpdateTag = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, ...updateData }: Partial<Tag> & { id: string }): Promise<Tag> => {
      console.log('📝 Atualizando tag:', id);

      const { data, error } = await supabase
        .from('tags')
        .update({
          ...updateData,
          updated_at: new Date().toISOString(),
        })
        .eq('id', id)
        .select()
        .single();

      if (error) {
        console.error('❌ Erro ao atualizar tag:', error);
        throw new Error(`Erro ao atualizar tag: ${error.message}`);
      }

      console.log('✅ Tag atualizada com sucesso:', data.nome);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tags'] });
      toast.success('Tag atualizada com sucesso!');
    },
    onError: (error: Error) => {
      console.error('❌ Erro na atualização da tag:', error);
      toast.error(`Erro ao atualizar tag: ${error.message}`);
    },
  });
};

// Hook para deletar tag
export const useDeleteTag = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (tagId: string): Promise<void> => {
      console.log('🗑️ Deletando tag:', tagId);

      const { data, error } = await supabase
        .from('tags')
        .delete()
        .eq('id', tagId);

      if (error) {
        console.error('❌ Erro ao deletar tag:', error);
        throw new Error(`Erro ao deletar tag: ${error.message}`);
      }

      console.log('✅ Tag deletada com sucesso');
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tags'] });
      toast.success('Tag deletada com sucesso!');
    },
    onError: (error: Error) => {
      console.error('❌ Erro na exclusão da tag:', error);
      toast.error(`Erro ao deletar tag: ${error.message}`);
    },
  });
};

/**
 * Hook para deletar múltiplas tags simultaneamente
 * 
 * O que faz:
 * - Recebe array de IDs de tags
 * - Executa DELETE com operador IN do Supabase (eficiente)
 * - Invalida cache e mostra toast de sucesso/erro
 * 
 * Onde é usado:
 * - TagManager.tsx (botão "Excluir Selecionadas")
 */
export const useDeleteMultipleTags = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (tagIds: string[]): Promise<void> => {
      console.log(`🗑️ Deletando ${tagIds.length} tags em lote`);

      const { error } = await supabase
        .from('tags')
        .delete()
        .in('id', tagIds);

      if (error) {
        console.error('❌ Erro ao deletar tags em lote:', error);
        throw new Error(`Erro ao deletar tags: ${error.message}`);
      }

      console.log(`✅ ${tagIds.length} tags deletadas com sucesso`);
    },
    onSuccess: (_, tagIds) => {
      queryClient.invalidateQueries({ queryKey: ['tags'] });
      toast.success(`${tagIds.length} tag(s) deletada(s) com sucesso!`);
    },
    onError: (error: Error) => {
      console.error('❌ Erro na exclusão em lote:', error);
      toast.error(`Erro ao deletar tags: ${error.message}`);
    },
  });
};

// Exportar useTagsData como alias para useTags (compatibilidade)
export const useTagsData = useTags;
